import type { CreatePatientFormData } from '../../schemas/createPatientFormSchema';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import type { CreateEpisodeStepFormData } from '../../schemas/createEpisodeStepFormSchema';

// Language options for Kinnser dropdown
export const LANGUAGE_OPTIONS = [
  { value: '1', label: 'English' },
  { value: '2', label: 'Spanish' },
  { value: '3', label: 'Chinese' },
  { value: '4', label: 'Russian' },
  { value: '5', label: 'Vietnamese' },
  { value: '6', label: 'Other/Unknown' },
];

// Admission type options for Kinnser dropdown
export const ADMISSION_TYPE_OPTIONS = [
  { value: '1', label: 'Emergency' },
  { value: '2', label: 'Urgent' },
  { value: '3', label: 'Elective' },
  { value: '4', label: 'Newborn' },
  { value: '5', label: 'Trauma' },
  { value: '6', label: 'Information not Available' },
];

// Map from ADMISSION_TYPE_OPTIONS label to value
export const ADMISSION_TYPE_VALUE_BY_LABEL: Record<string, string> =
  ADMISSION_TYPE_OPTIONS.reduce(
    (acc, option) => {
      acc[option.label] = option.value;
      return acc;
    },
    {} as Record<string, string>
  );

// HCPCS code options for Kinnser dropdown
export const HCPCS_CODE_OPTIONS = [
  { value: '25', label: "(Q5001) Care provided in patient's home/residence" },
  { value: '26', label: '(Q5002) Care provided in assisted living facility' },
  {
    value: '27',
    label: '(Q5009) Care provided in place not otherwise specified (NOS)',
  },
];

// Language key for "Other/Unknown"
export const OTHER_LANGUAGE_KEY = '6';

// Default form values for original form
export const FORM_DEFAULT_VALUES: CreatePatientFormData = {
  // Clinic selection
  agency_clinic_id: null,
  kinnser_physician_id: null,
  kinnser_branch_id: null,
  kinnser_insurance_provider_id: null,
  kinnser_admission_type: null,
  kinnser_hcpcs_code_key: null,
  kinnser_mrn: null,
  kinnser_hh_cahps_opt_out: null,
  kinnser_language_key: null,
  kinnser_other_language: null,
  // Episode creation flags
  kinnser_create_episode: null,
  roger_create_episode: null,
  roger_physician_id: null,
  push_to_kinnser: null,
  push_to_roger: null,
  // Patient Identification
  patient_first_name: null,
  patient_middle_name: null,
  patient_last_name: null,
  patient_suffix: null,
  patient_dob: null,
  patient_gender: null,
  patient_ssn: null,
  patient_primary_language: null,
  // Contact Information
  patient_phone: null,
  patient_secondary_phone: null,
  patient_email: null,
  patient_street_address: null,
  patient_city: null,
  patient_state: null,
  patient_zip_code: null,
  patient_county: null,
  // Emergency contact (optional; pushed to WellSky when create/onboard)
  emergency_contact_name: null,
  emergency_contact_relationship: null,
  emergency_contact_phone: null,
  emergency_contact_street_address: null,
  emergency_contact_city: null,
  emergency_contact_state: null,
  emergency_contact_zip_code: null,
  // Insurance Information
  medicaid_id: null,
  // Notes
  patient_notes: null,
};

// Default values for scheduling step form
export const SCHEDULING_STEP_DEFAULT_VALUES: SchedulingStepFormData = {
  kinnser_create_episode: null,
  roger_create_episode: null,
  push_to_kinnser: null,
  push_to_roger: null,
  kinnser_visit_type: null,
  kinnser_creation_type: null,
  roc_date: null,
};

// Default values for create episode step form
export const CREATE_EPISODE_STEP_DEFAULT_VALUES: CreateEpisodeStepFormData = {
  // Push to EMR
  push_to_roger: null,
  push_to_kinnser: null,
  has_transferred_from_another_hha: null,
  prior_hha: null,
  f2f_encounter_date: null,
  // Clinic selection
  agency_clinic_id: null,
  // Kinnser Information
  kinnser_physician_id: null,
  kinnser_branch_id: null,
  kinnser_insurance_provider_id: null,
  kinnser_admission_type: null,
  kinnser_hcpcs_code_key: null,
  kinnser_mrn: null,
  kinnser_hh_cahps_opt_out: null,
  kinnser_language_key: null,
  kinnser_other_language: null,
  // Roger physician
  roger_physician_id: null,
  // Patient Identification
  patient_first_name: null,
  patient_middle_name: null,
  patient_last_name: null,
  patient_suffix: null,
  patient_dob: null,
  patient_gender: null,
  patient_ssn: null,
  patient_primary_language: null,
  // Contact Information
  patient_phone: null,
  patient_secondary_phone: null,
  patient_email: null,
  patient_street_address: null,
  patient_city: null,
  patient_state: null,
  patient_zip_code: null,
  patient_county: null,
  // Emergency contact (optional; pushed to WellSky when create/onboard)
  emergency_contact_name: null,
  emergency_contact_relationship: null,
  emergency_contact_phone: null,
  emergency_contact_street_address: null,
  emergency_contact_city: null,
  emergency_contact_state: null,
  emergency_contact_zip_code: null,
  // Insurance Information
  medicaid_id: null,
  // Notes
  patient_notes: null,
};
